import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaChevronDown, FaSearch } from "react-icons/fa";

const SEARCH_THRESHOLD = 8;

function textOf(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) return textOf(node.props.children);
  return "";
}

function collectOptions(children, out = []) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      collectOptions(child.props.children, out);
      return;
    }
    if (child.type !== "option") return;
    const label = textOf(child.props.children).trim();
    const value = child.props.value != null ? String(child.props.value) : label;
    out.push({ value, label, disabled: !!child.props.disabled });
  });
  return out;
}

/**
 * Drop-in replacement for a native <select> with <option> children.
 * onChange receives an event-like object so `e.target.value` handlers keep working.
 */
function PortalSelect({
  value,
  onChange,
  disabled = false,
  required = false,
  name,
  id,
  className = "",
  children,
  searchable,
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const options = useMemo(() => collectOptions(children), [children]);
  const current = value != null ? String(value) : "";
  const placeholder = options.find((o) => o.value === "");
  const choices = options.filter((o) => o.value !== "");
  const selected = choices.find((o) => o.value === current);
  const showSearch = searchable ?? choices.length >= SEARCH_THRESHOLD;
  const canClear = !required && !!selected && !!placeholder;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? choices.filter((o) => o.label.toLowerCase().includes(q)) : choices;
  }, [choices, query]);

  const close = (refocus = false) => {
    setOpen(false);
    setQuery("");
    if (refocus) triggerRef.current?.focus();
  };

  const pick = (option) => {
    if (option.disabled) return;
    close(true);
    if (option.value === current) return;
    onChange?.({
      target: { value: option.value, name },
      currentTarget: { value: option.value, name },
    });
  };

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return undefined;
    setPortalTarget(rootRef.current.closest(".cportal") || document.body);
    const place = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = 6;
      const width = Math.max(rect.width, 200);
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
      setMenuStyle({
        position: "fixed",
        left: Math.min(Math.max(8, rect.left), window.innerWidth - width - 8),
        width,
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
        maxHeight: Math.min(320, (openUp ? spaceAbove : spaceBelow) - gap),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.value === current);
    setActiveIndex(idx >= 0 ? idx : filtered.length ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  useEffect(() => {
    if (open && menuStyle && showSearch) searchRef.current?.focus();
  }, [open, menuStyle, showSearch]);

  useEffect(() => {
    if (!open || activeIndex < 0 || !menuRef.current) return;
    const el = menuRef.current.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const moveActive = (delta) => {
    if (!filtered.length) return;
    setActiveIndex((i) => {
      let next = i;
      for (let step = 0; step < filtered.length; step += 1) {
        next = (next + delta + filtered.length) % filtered.length;
        if (!filtered[next].disabled) return next;
      }
      return i;
    });
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveActive(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveActive(-1);
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[activeIndex]) pick(filtered[activeIndex]);
        break;
      case "Escape":
        e.preventDefault();
        close(true);
        break;
      case "Tab":
        close();
        break;
      default:
    }
  };

  return (
    <div
      ref={rootRef}
      className={`cportal-select${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}${
        className ? ` ${className}` : ""
      }`}
      onKeyDown={onKeyDown}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="cportal-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className={`cportal-select-value${selected ? "" : " is-placeholder"}`}>
          {selected ? selected.label : placeholder?.label || "Select…"}
        </span>
        <FaChevronDown className="cportal-select-caret" aria-hidden />
      </button>
      {required ? (
        <input
          className="cportal-select-native"
          tabIndex={-1}
          aria-hidden
          required
          value={selected ? current : ""}
          onChange={() => {}}
          onFocus={() => triggerRef.current?.focus()}
        />
      ) : null}

      {open && menuStyle && portalTarget
        ? createPortal(
            <div ref={menuRef} className="cportal-select-menu" style={menuStyle}>
              {showSearch ? (
                <div className="cportal-select-search">
                  <FaSearch aria-hidden />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search…"
                    aria-label="Search options"
                  />
                </div>
              ) : null}
              <ul id={listId} role="listbox" className="cportal-select-list" aria-label={ariaLabel}>
                {canClear && !query ? (
                  <li
                    role="option"
                    aria-selected={false}
                    className="cportal-select-option cportal-select-option--clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(placeholder)}
                  >
                    {placeholder.label}
                  </li>
                ) : null}
                {filtered.map((option, index) => {
                  const isSelected = option.value === current;
                  return (
                    <li
                      key={option.value}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      className={`cportal-select-option${isSelected ? " is-selected" : ""}${
                        index === activeIndex ? " is-active" : ""
                      }${option.disabled ? " is-disabled" : ""}`}
                      onMouseEnter={() => !option.disabled && setActiveIndex(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(option)}
                    >
                      <span className="cportal-select-option-label">{option.label}</span>
                      {isSelected ? <FaCheck className="cportal-select-check" aria-hidden /> : null}
                    </li>
                  );
                })}
                {!filtered.length ? (
                  <li className="cportal-select-empty">{query ? "No matches found" : "No options"}</li>
                ) : null}
              </ul>
            </div>,
            portalTarget
          )
        : null}
    </div>
  );
}

export default PortalSelect;
